// components/StudentPortal/Task/useStudentTask.js
// Datos del wizard de una tarea (S2, F5). Carga el progreso (para el stepper) y
// el siguiente paso (contenido actual, con meta de escena REAL desde el backend
// F5: sceneTitle/sceneBanner/sceneEnterUrl). Reutiliza TeacherTasksAPI.
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../hooks";
import { TeacherTasksAPI } from "../../../api/teacherTasksAPI";
import { MozillaHubRoomsApi } from "../../../api";

const tasksApi = new TeacherTasksAPI();
const hubRoomsApi = new MozillaHubRoomsApi();

export function useStudentTask(taskId) {
  const { user, accessToken } = useAuth();
  const studentId = user?.user?.student;

  const [state, setState] = useState({
    loading: true,
    error: null,
    task: null,
    steps: [],
    finalQuizRequired: false,
    finalQuizDone: false,
    finalQuizId: null,
    assistant: null, // { id, title, miniature } | null (asistente IA de la tarea)
    nextStep: null, // { stepId, sceneTitle, sceneBanner, sceneEnterUrl, instruction } | null
    allDone: false,
  });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!accessToken || !studentId || !taskId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const progRes = await tasksApi.getStudentTaskProgress(
        accessToken,
        studentId,
        taskId,
      );
      const progress = progRes?.progress || {};
      const task = progress.task || {};

      const nextRes = await tasksApi.getNextStep(
        accessToken,
        studentId,
        taskId,
      );
      const next = nextRes?.next || null;

      setState({
        loading: false,
        error: null,
        task,
        steps: Array.isArray(progress.steps) ? progress.steps : [],
        finalQuizRequired: !!task.finalQuizRequired,
        finalQuizDone: !!(progress.finalQuiz && progress.finalQuiz.done),
        finalQuizId: task.finalQuiz || null,
        assistant: task.assistant || null,
        nextStep: next,
        allDone: next === null,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e?.message || "error" }));
    }
  }, [accessToken, studentId, taskId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitStep = useCallback(
    async (mode, text) => {
      const stepId = state.nextStep?.stepId;
      if (!stepId) return;
      setSending(true);
      try {
        await tasksApi.submitStepAnswer(
          accessToken,
          studentId,
          taskId,
          stepId,
          {
            mode,
            text,
          },
        );
        await load();
      } catch (e) {
        setState((s) => ({ ...s, error: e?.message || "submit-error" }));
      } finally {
        setSending(false);
      }
    },
    [accessToken, studentId, taskId, state.nextStep, load],
  );

  // Metrica de apertura de la sala del paso. La sala compartida de la tarea se
  // provisiona UNA vez y la reutiliza toda la clase, asi que la entrada del
  // alumno no toca el backend de Hubs: sin esta baliza no se veria en el
  // dashboard. Es USO en clase.
  const trackStepSceneOpen = useCallback(() => {
    const next = state.nextStep;
    if (!accessToken || !next?.sceneEnterUrl) return;
    hubRoomsApi.trackRoomOpen(accessToken, {
      sceneRefId: next.scene,
      url: next.sceneEnterUrl,
      title: next.sceneTitle,
      source: "task_student",
    });
  }, [accessToken, state.nextStep]);

  return { ...state, sending, reload: load, submitStep, trackStepSceneOpen };
}
